/**
 * /share/:slug — PUBLIC shared-song page. No authentication.
 *
 * What a recipient gets when someone sends them a Lyrixa link: the artwork, the
 * title, the genre, the metadata the owner exposed, and — once audio exists — the
 * ability to listen. Mounted outside the app shell, like /login and
 * /license/verify: a recipient has no sidebar, no library and no account, and
 * rendering the signed-in chrome around a stranger's view would be wrong.
 *
 * What it deliberately does NOT show: the prompt the song was generated from, what
 * it cost, its version history, its favourite state, or its download count.
 * Sharing a track is not handing over the project. That boundary is enforced in
 * shareApi.SharedSong, which is a narrower type than Song, rather than by this
 * page choosing what to render from a full record.
 *
 * ============================================================================
 * NOTHING RESOLVES YET, for two independent reasons — and the page's job is to be
 * correct about both rather than to find something to draw.
 *
 * 1. VISIBILITY. shareApi.getShared resolves a song only when its owner made it
 *    public or unlisted (shareApi.isLinkAccessible). The seed library is entirely
 *    private, so a slug resolves only for a track somebody actually published
 *    through the Library's "Make discoverable" action. A private song and a
 *    nonexistent slug produce the SAME null and the same page here, deliberately:
 *    distinguishing them would confirm to someone guessing slugs that a private
 *    song exists.
 * 2. NO SERVER. Even for an accessible song the lookup searches the mock's songs
 *    table, which is this browser's memory — so it could only ever answer in the
 *    browser that already has the song: the sender's, not the recipient's.
 *    Structurally correct page, unreal resolution, exactly as with /license/verify.
 *    This page does NOT pass a library any more (Batch 7 §1.6) and no longer reads
 *    the account's store at all, which is the right shape for a public page even
 *    though it does not make the resolution real.
 *
 * There is also nothing to listen to: `audioUrl` is null for every song because
 * generation is simulated. So this page offers no player rather than a dead play
 * button, and says why. When audio is real, the `audioUrl` branch below is where
 * the player goes. See docs/api-contract.md §22.
 * ============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { Bookmark, ChevronDown, ExternalLink, Heart, Link2, Music, User } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { UNKNOWN_API_ERROR_MESSAGE, toApiError } from '../api/errors';
import { shareApi } from '../api/shareApi';
import type { SharedSong } from '../api/shareApi';
import { USE_MOCK_API } from '../config';
import ArtworkPlaceholder from '../components/songs/ArtworkPlaceholder';
import LyricSheet from '../components/songs/LyricSheet';
import { LoadError, PrototypeNotice } from '../components/ui';
import { playbackApi } from '../api/playbackApi';
import DeliveryAudio from '../components/player/DeliveryAudio';
import { attributionForName } from '../lib/attribution';
import { joinWithMiddleDot } from '../lib/formatting';
import { shareUrl } from '../lib/share';
import { formatCount, loveCountLabel } from '../lib/engagement';
import { useAuthStore } from '../stores/authStore';
import { c } from '../styles/theme';
import { paths } from './paths';

/** Resolution state. 'loading' is the initial state, before the lookup settles. */
type Status = 'loading' | 'found' | 'missing' | 'unavailable';

export default function Share() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const sessionChecked = useAuthStore((s) => s.sessionChecked);
  const restoreSession = useAuthStore((s) => s.restoreSession);

  const [status, setStatus] = useState<Status>('loading');
  const [shared, setShared] = useState<SharedSong | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [lyricsOpen, setLyricsOpen] = useState(false);
  const [pendingIntent, setPendingIntent] = useState<'love' | 'save' | null>(null);
  const [lyricElapsedSec, setLyricElapsedSec] = useState<number | null>(null);
  const [lyricDurationSec, setLyricDurationSec] = useState<number | null>(null);
  const [lyricPlaybackActive, setLyricPlaybackActive] = useState(false);

  useEffect(() => {
    if (!sessionChecked && !isAuthenticated) void restoreSession();
  }, [sessionChecked, isAuthenticated, restoreSession]);

  useEffect(() => {
    if (!slug || !isAuthenticated) return;
    const qs = new URLSearchParams({ shared: slug });
    if (pendingIntent) qs.set('intent', pendingIntent);
    navigate(`${paths.dashboard}?${qs.toString()}`, { replace: true });
  }, [slug, isAuthenticated, navigate, pendingIntent]);

  useEffect(() => {
    setLyricsOpen(false);
    setLyricElapsedSec(null);
    setLyricDurationSec(null);
    setLyricPlaybackActive(false);
  }, [slug]);

  useEffect(() => {
    if (!slug) {
      setShared(null);
      setErrorMessage(null);
      setStatus('missing');
      return;
    }
    if (!sessionChecked && !isAuthenticated) {
      setShared(null);
      setErrorMessage(null);
      setStatus('loading');
      return;
    }
    if (isAuthenticated) {
      setShared(null);
      setErrorMessage(null);
      setStatus('loading');
      return;
    }
    let active = true;
    setStatus('loading');
    setShared(null);
    setErrorMessage(null);
    /*
      NO LIBRARY IS PASSED (Batch 7 §1.6). This page used to subscribe to songsStore and hand
      `songs` to `getShared`, which meant a PUBLIC page resolved a link out of the signed-in
      user's own library — the browser deciding what a share link may reach. The lookup now
      happens behind the boundary, against the catalogue.

      The subscription went with the argument, and its removal is the more important half: a
      page mounted outside the app shell for a recipient with no account had no business
      reading the account's library at all.
    */
    void shareApi
      .getShared(slug)
      .then((result) => {
        // The lookup is async, so a slug change mid-flight would otherwise let a
        // stale result overwrite a newer one.
        if (!active) return;
        setShared(result);
        setStatus(result ? 'found' : 'missing');
      })
      .catch((err) => {
        if (!active) return;
        setShared(null);
        setErrorMessage(toApiError(err, { preserveMessage: false }).message);
        setStatus('unavailable');
      });
    return () => {
      active = false;
    };
    /*
      The lookup now depends on the auth answer too: while that answer is unresolved the public
      page waits, and a signed-in answer short-circuits the lookup in favour of the redirect to
      Home. `reloadKey` remains the explicit retry path for the public resolver.
    */
  }, [slug, reloadKey, sessionChecked, isAuthenticated]);

  // Null before the lookup settles and null for a song whose record names no creator, which
  // is what omits the credit line rather than drawing an empty one. Read from the resolved
  // payload only — there is no account on this page to fall back to, and no fallback if there
  // were. See the line itself.
  const attribution = attributionForName(shared?.creatorName);

  /*
    WHETHER THERE IS ANYTHING TO LISTEN TO, from the boundary rather than from a field read
    (Batch 7 §9). `shared` is null while the lookup runs and for an unresolvable slug, which
    `support` accepts — the answer for "no song" is the same as for "no audio", and both are
    "nothing to play" plus the reasons why.
  */
  const playback = playbackApi.support(shared);
  const trackInfo = shared
    ? joinWithMiddleDot([
        shared.length,
        `${shared.bpm} BPM`,
        shared.mood,
        shared.language,
        shared.date,
        shared.coverStyle,
      ])
    : '';
  const hasLyrics = Boolean(shared?.lyrics.trim());
  const loves = useMemo(
    () => (shared ? (shared.loveCount ?? 0) + (shared.favorite ? 1 : 0) : 0),
    [shared]
  );

  function continueIntoApp(intent: 'love' | 'save') {
    if (!slug) return;
    if (isAuthenticated) {
      setPendingIntent(intent);
      return;
    }
    const qs = new URLSearchParams({ shared: slug, intent });
    navigate(paths.login, { state: { afterLogin: `${paths.dashboard}?${qs.toString()}` } });
  }

  return (
    <div className="lyx-share-page">
      {/* ---- brand header. Not the app's sidebar: this visitor is not a user. ---- */}
      <header style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 22 }}>
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            background: c.accentFill,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Music size={14} color="#1A1300" aria-hidden="true" />
        </span>
        <span
          style={{
            fontFamily: 'Space Grotesk',
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: '0.02em',
            color: c.text1,
          }}
        >
          Lyrixsa AI
        </span>
      </header>

      {status === 'loading' && (
        // A live region: the one thing on screen while the link resolves.
        <p role="status" style={{ fontSize: 13, color: c.text2 }}>
          Opening shared track…
        </p>
      )}

      {status === 'missing' && (
        <div
          style={{
            background: c.surface1,
            border: `1px solid ${c.border}`,
            borderRadius: 12,
            padding: '28px 24px',
            textAlign: 'center',
          }}
        >
          <h1
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 16,
              margin: '0 0 6px',
              color: c.text1,
            }}
          >
            This link isn&rsquo;t available
          </h1>
          {/* Every reason, in one sentence, and none of them naming which one applies:
              "this track is private" would confirm the track exists to anyone trying
              slugs. See the header. */}
          <p style={{ fontSize: 12.5, color: c.text2, margin: '0 0 4px', lineHeight: 1.6 }}>
            The track may be private, may have been deleted, or the link may be incomplete.
          </p>
          {USE_MOCK_API && (
            /* The old mock limitation remains true in the fixture-backed test path only. */
            <p style={{ fontSize: 11.5, color: c.text2, margin: 0, fontStyle: 'italic' }}>
              Share links are not hosted yet — tracks stay private until their owner shares them,
              and there is no server to resolve a link for anyone else.
            </p>
          )}
        </div>
      )}

      {status === 'unavailable' && (
        <LoadError
          message={errorMessage ?? UNKNOWN_API_ERROR_MESSAGE}
          onRetry={() => setReloadKey((n) => n + 1)}
        >
          <p style={{ fontSize: 12, color: c.text2, margin: '8px 0 0', lineHeight: 1.55 }}>
            We could not reach Lyrixsa&rsquo;s share service. The link itself may still be valid —
            try again in a moment.
          </p>
        </LoadError>
      )}

      {status === 'found' && shared && (
        <article
          style={{
            background: c.surface1,
            border: `1px solid ${c.border}`,
            borderRadius: 12,
            padding: 20,
          }}
        >
          <div className="lyx-share-hero">
            {/* The same artwork the owner sees, resolved by the same rules — an
                uploaded image if there is one, otherwise the gradient from the seed
                shareApi already put through lib/artwork. A shared page showing the
                recipient different artwork than the sender would be a bug. */}
            <div className="lyx-share-art" style={{ background: c.surface2 }}>
              {shared.coverUrl ? (
                <img
                  src={shared.coverUrl}
                  alt={`Cover art for ${shared.title}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 11 }}
                />
              ) : (
                <ArtworkPlaceholder
                  id={shared.artworkSeed}
                  depth="hero"
                  style={{ width: '100%', height: '100%', borderRadius: 11 }}
                />
              )}
            </div>

            <div
              style={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                minHeight: 132,
              }}
            >
              <p
                style={{
                  fontSize: 10,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: c.accent,
                  margin: 0,
                }}
              >
                Shared track
              </p>
              <h1
                style={{
                  fontFamily: 'Space Grotesk',
                  fontWeight: 600,
                  fontSize: 24,
                  lineHeight: 1.2,
                  margin: 0,
                  color: c.text1,
                }}
              >
                {shared.title}
              </h1>
              <p style={{ fontSize: 13, color: c.text2, margin: 0 }}>{shared.genre}</p>
              {/*
                ---- CREATOR ATTRIBUTION ---- (Batch 7 §5)
                "Created by Goodluck with Lyrixa AI", from lib/attribution — the same module
                and the same sentence as the featured hero, the trending rows and the explore
                tiles, so a song is credited identically wherever it is seen.

                THIS IS THE SURFACE THE OMISSION MATTERED MOST ON. A recipient arrives here
                from a link with no account, no catalogue and no other context: the creator is
                the one thing they most need to be told, and it was the one thing the page did
                not say. It sits above the app link because who made it precedes what they can
                do next.

                `attributionForName` RATHER THAN `attributionFor`, because this page holds a
                `SharedSong` and not a `Song` — the public payload is narrower on purpose. The
                name is projected onto it explicitly in shareApi (and `creatorId` is not), so
                publishing it was a decision rather than a spread. Same template, same first
                name only, same null.

                NO NAME IS WRITTEN HERE AND THERE IS NOTHING TO FALL BACK TO. The helper
                returns null when the record names no creator, which omits the element — and
                on this page a fallback to the signed-in user would be at its most wrong,
                since the reader is a stranger who is not signed in at all.
              */}
              {attribution && (
                <p
                  style={{
                    fontSize: 12,
                    color: c.text2,
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <User size={12} aria-hidden="true" style={{ flexShrink: 0 }} />
                  {attribution}
                </p>
              )}
              {trackInfo && (
                <p style={{ fontSize: 11.5, color: c.text2, margin: 0, lineHeight: 1.4 }}>
                  {trackInfo}
                </p>
              )}
              <Link
                to={paths.login}
                style={{
                  marginTop: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  alignSelf: 'flex-start',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 999,
                  border: `1px solid ${c.border}`,
                  background: c.surface2,
                  color: c.text1,
                  textDecoration: 'none',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Visit App
                <ExternalLink size={12} aria-hidden="true" />
              </Link>
            </div>
          </div>

          <section aria-label="Lyrics" style={{ marginTop: 22 }}>
            <button
              type="button"
              aria-expanded={lyricsOpen}
              aria-controls="share-lyrics-panel"
              onClick={() => setLyricsOpen((open) => !open)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: c.text2,
                  fontWeight: 500,
                }}
              >
                Lyrics
              </span>
              <ChevronDown
                size={14}
                aria-hidden="true"
                color={c.text2}
                style={{
                  flexShrink: 0,
                  transform: lyricsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 160ms ease',
                }}
              />
            </button>
            <div id="share-lyrics-panel" style={{ marginTop: lyricsOpen ? 10 : 0 }}>
              {lyricsOpen ? (
                hasLyrics ? (
                  <LyricSheet
                    lyrics={shared.lyrics}
                    at={
                      lyricPlaybackActive &&
                      lyricDurationSec &&
                      lyricDurationSec > 0 &&
                      lyricElapsedSec !== null
                        ? lyricElapsedSec / lyricDurationSec
                        : null
                    }
                    elapsedSec={lyricPlaybackActive ? lyricElapsedSec : null}
                    lyricTimings={shared.lyricTimings}
                    lyricSectionTimings={shared.lyricSectionTimings}
                    className="lyx-lyrics-sheet"
                  />
                ) : (
                  <p style={{ fontSize: 12.5, color: c.text2, margin: 0, lineHeight: 1.7 }}>
                    Lyrics aren&rsquo;t available for this song yet.
                  </p>
                )
              ) : null}
            </div>
          </section>

          {/* ---- listening ----
              THE SEAM FOR A REAL PLAYER, and the app's only consumer of a real URL — so it
              goes through api/playbackApi rather than reading the field inline (Batch 7 §9,
              which puts this question at the service layer instead of in the components).
              `playback.audioUrl` is whatever the payload carried, which is null for every
              song today, so what renders is the explanation rather than a control that
              cannot work.

              The reasons come from the boundary rather than being restated here, the way
              UploadPanel renders audioUploadApi's, so this copy cannot drift from what the
              boundary reports — and it shortens on its own the day audio is real. */}
          {playback.audioUrl ? (
            <DeliveryAudio
              controls
              src={playback.audioUrl}
              style={{ width: '100%', marginTop: 18 }}
              aria-label={`Play ${shared.title}`}
              onLoadedMetadata={(event) => {
                const duration = event.currentTarget.duration;
                setLyricDurationSec(Number.isFinite(duration) && duration > 0 ? duration : null);
              }}
              onPlay={() => setLyricPlaybackActive(true)}
              onTimeUpdate={(event) => {
                const { currentTime, duration } = event.currentTarget;
                setLyricElapsedSec(currentTime);
                setLyricDurationSec(Number.isFinite(duration) && duration > 0 ? duration : null);
                if (currentTime > 0) setLyricPlaybackActive(true);
              }}
            />
          ) : (
            <PrototypeNotice style={{ marginTop: 18 }}>
              Playback isn&rsquo;t available on shared pages yet.{' '}
              {playback.blockers.map((blocker) => blocker.summary).join(' ')} The track&rsquo;s
              lyrics and details above are real.
            </PrototypeNotice>
          )}

          <div
            style={{
              marginTop: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <p
              style={{
                fontSize: 11,
                color: c.text2,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: 'IBM Plex Mono',
                wordBreak: 'break-all',
                minWidth: 0,
                flex: '1 1 260px',
              }}
            >
              <Link2 size={12} aria-hidden="true" style={{ flexShrink: 0 }} />
              {shareUrl(shared.slug)}
            </p>

            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => continueIntoApp('love')}
                aria-label={`Love ${shared.title} — ${loveCountLabel(loves)}`}
                title={`Love ${shared.title}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  borderRadius: 999,
                  border: `1px solid ${c.border}`,
                  background: c.surface2,
                  color: c.text1,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                <Heart size={13} aria-hidden="true" />
                <span aria-hidden="true">{formatCount(loves)}</span>
              </button>

              <button
                type="button"
                onClick={() => continueIntoApp('save')}
                aria-label={`Save ${shared.title} to a playlist`}
                title={`Save ${shared.title} to a playlist`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  borderRadius: 999,
                  border: `1px solid ${c.border}`,
                  background: c.surface2,
                  color: c.text1,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                <Bookmark size={13} aria-hidden="true" />
                Save
              </button>
            </div>
          </div>
        </article>
      )}
    </div>
  );
}
